import { useMemo } from "react";
import type uPlot from "uplot";

import { chartTheme } from "./chartTheme";
import { escapeeSummary, findEscapees, type BandPoint } from "./outliers";
import { UPlotChart } from "./UPlotChart";
import { timeAxisValues } from "./timeAxis";

export interface BandOutlierChartProps {
  label: string;
  unit: string;
  times: number[];
  values: (number | null)[];
  lo: number[];
  hi: number[];
  selectedIdx?: number;
  height?: number;
}

export function BandOutlierChart({
  label,
  unit,
  times,
  values,
  lo,
  hi,
  selectedIdx,
  height,
}: BandOutlierChartProps) {
  const theme = chartTheme();

  const { data, summary } = useMemo(() => {
    const points: BandPoint[] = times.map((t, i) => ({
      t,
      v: values[i] ?? null,
      lo: lo[i] ?? 0,
      hi: hi[i] ?? 0,
    }));
    const escapees = findEscapees(points);
    const escapeeIdx = new Set(escapees.map((e) => e.index));
    const escapeeY = values.map((v, i) => (escapeeIdx.has(i) ? v : null));
    const aligned: uPlot.AlignedData = [times, lo, hi, values, escapeeY];
    return { data: aligned, summary: `${label}: ${escapeeSummary(points, unit)}` };
  }, [label, unit, times, values, lo, hi]);

  const options = useMemo<Omit<uPlot.Options, "width" | "height">>(
    () => ({
      scales: { x: { time: true } },
      legend: { show: false },
      axes: [
        // X axis: readable labels (`stroke` colours the tick TEXT in uPlot, so the
        // faint theme.axis is kept only for the tick marks; see PlanVsDelivered).
        {
          stroke: theme.textMuted,
          // 24-hour ticks, like every other time in the product.
          values: timeAxisValues,
          font: "600 12px var(--sk-font-text, sans-serif)",
          grid: { stroke: theme.grid, width: 1 },
          ticks: { stroke: theme.axis },
        },
        { stroke: theme.textMuted, grid: { stroke: theme.grid, width: 1 }, ticks: { show: false } },
      ],
      bands: [{ series: [2, 1], fill: theme.band }],
      series: [
        {},
        { stroke: theme.bandEdge, width: 1, points: { show: false } },
        { stroke: theme.bandEdge, width: 1, points: { show: false } },
        {
          label,
          stroke: selectedIdx != null ? theme.selected : theme.measured,
          width: 2,
          points: { show: false },
        },
        {
          stroke: theme.escapeeLow,
          width: 0,
          points: { show: true, size: 6, stroke: theme.escapeeLow, fill: theme.escapeeLow },
        },
      ],
    }),
    [label, theme, selectedIdx],
  );

  return (
    <div className="card">
      <div className="mb-2 font-medium">{label}</div>
      <UPlotChart
        options={options}
        data={data}
        height={height ?? 200}
        ariaLabel={`${label} with expected band`}
        summary={summary}
      />
    </div>
  );
}
